import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// MembersPanel.tsx -- floating panel listing the project's human members.
// Sibling to CoworkersPanel. Opened from the + menu's "Members" item.
//
// Per the user's "for each person the other person just looks like an
// agent" mental model: the panel reuses the CoworkersPanel pattern
// exactly. Each row shows initials/avatar + name + role chip + email.
// Click a row -> useAgent.setActiveMember arms the next session as a
// human DM (participants:[{type:'user', id}]) and opens the chat
// window. The BFF detects no coworker entry and routes the session
// through the human-only branch (no agent loop).
//
// Invite / revoke are deferred for now; the footer button is a stub
// that will eventually open a small invite-by-email modal. Existing
// memberships are managed through raindb-app.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../utils/api.js';
import { useAuth } from '../../hooks/useAuth.js';
import { RecipientHistoryPopover } from '../Chat/RecipientHistoryPopover.js';
function getInitials(s) {
    if (!s)
        return '?';
    const parts = s.trim().split(/\s+/);
    if (parts.length === 1)
        return s.slice(0, 2).toUpperCase();
    return (parts[0][0] ?? '?').toUpperCase() + (parts[parts.length - 1][0] ?? '').toUpperCase();
}
export function MembersPanel({ projectId, open, onClose }) {
    const { user } = useAuth();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const refresh = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const r = await api.get(`/p/${projectId}/members`);
            setMembers(r.members ?? []);
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : 'failed to load members');
        }
        finally {
            setLoading(false);
        }
    }, [projectId]);
    useEffect(() => {
        if (open)
            void refresh();
    }, [open, refresh]);
    // Per user direction (May 2026): single-click a member opens the
    // History popover scoped to that member, NOT a chat window. FDN's
    // messaging model is topic/project-based -- conversations are
    // discrete subjects, not a single running stream with a person.
    // Picking the member is the first step ("with whom"); choosing the
    // topic happens in the History popover ("which conversation").
    const [historyForMember, setHistoryForMember] = useState(null);
    const [historyAnchor, setHistoryAnchor] = useState(null);
    const onPickMember = useCallback((m, ev) => {
        if (user && m.userId === user.userId) {
            setErr("That's you. Pick someone else.");
            return;
        }
        if (m.status !== 'active') {
            setErr(`${m.name ?? m.email ?? 'member'} is not active yet.`);
            return;
        }
        const rect = ev.currentTarget.getBoundingClientRect();
        setHistoryAnchor({ x: rect.x, y: rect.y, width: rect.width, height: rect.height });
        setHistoryForMember(m);
    }, [user]);
    const historyRecipient = useMemo(() => {
        if (!historyForMember)
            return null;
        return {
            kind: 'user',
            userId: historyForMember.userId,
            name: historyForMember.name ?? historyForMember.email ?? historyForMember.userId,
            ...(historyForMember.email ? { email: historyForMember.email } : {}),
        };
    }, [historyForMember]);
    if (!open)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "modal-bg", onClick: onClose, children: _jsxs("div", { className: "coworkers-panel", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "coworkers-header", children: [_jsx("h3", { children: '\uD83D\uDC65 Members' }), _jsx("button", { type: "button", className: "ghost", onClick: onClose, title: "Close", children: '\u2715' })] }), _jsxs("div", { className: "coworkers-body", children: [loading && _jsx("div", { className: "coworkers-empty", children: 'Loading\u2026' }), err && _jsx("div", { className: "error", children: err }), !loading && !err && members.length === 0 && (_jsxs("div", { className: "coworkers-empty", children: [_jsx("p", { children: "No project members yet." }), _jsx("p", { style: { color: 'var(--fg-muted)', fontSize: 13 }, children: "Invite teammates from raindb-app group settings to get started." })] })), !loading && members.map((m) => {
                                    const isSelf = !!user && m.userId === user.userId;
                                    const display = m.name ?? m.email ?? m.userId;
                                    return (_jsx("div", { className: "coworkers-row", children: _jsxs("button", { type: "button", className: "coworkers-row-main", onClick: (e) => onPickMember(m, e), disabled: isSelf || m.status !== 'active', title: isSelf ? 'You' : `Show conversation history with ${display}`, children: [_jsx("span", { className: "coworkers-avatar member-avatar", "aria-hidden": true, children: getInitials(display) }), _jsxs("div", { className: "coworkers-row-text", children: [_jsxs("div", { className: "coworkers-row-name", children: [display, isSelf && _jsxs("span", { className: "member-self-tag", children: [" ", '\u00b7 you'] })] }), _jsxs("div", { className: "coworkers-row-skill", children: [_jsx("span", { className: `member-role-chip role-${m.role}`, children: m.role }), m.email && _jsx("span", { style: { marginLeft: 8, color: 'var(--fg-muted)' }, children: m.email }), m.status !== 'active' && (_jsxs("span", { style: { marginLeft: 8, color: 'var(--warning, #ffb86b)' }, children: ["(", m.status, ")"] }))] })] })] }) }, m.userId));
                                })] }), _jsx("div", { className: "coworkers-footer", children: _jsx("button", { type: "button", disabled: true, title: "Invite-by-email coming soon. For now, add members via raindb-app group settings.", children: "+ Invite member" }) })] }) }), historyRecipient && (_jsx(RecipientHistoryPopover, { projectId: projectId, recipient: historyRecipient, open: historyForMember !== null, anchorRect: historyAnchor, onClose: () => {
                    setHistoryForMember(null);
                    setHistoryAnchor(null);
                } }))] }));
}
